import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation as useLibraryTranslation, withTranslation as withLibraryTranslation } from 'react-i18next';
import hoistNonReactStatics from 'hoist-non-react-statics';

import ENV from 'config/env.conf';
import LSK from 'constants/core/localStorageKey.const';
import { determineDayRangeUTCForTimezone } from 'utils/converters/time/intl.util';
import { determineUserLocale, formatDateByLocale, formatTimeByLocale } from 'utils/date/format.util';
import lsSet from 'utils/dom/storage/lsSet.util';
import logger from 'utils/logger.util';
import fromComponentToName from 'utils/react/fromComponentToName.util';

import { useSalonGilt } from './SalonBasicInfo.provider';

export const LOCALE = Object.freeze({
  ae: 'en-AE',
  us: 'en-US',
  uk: 'en-GB',
});

const L = logger('GILTProvider');

export const DEFAULT_LOCALE = ENV.DEFAULT_LOCALE || LOCALE.uk;

const Context = React.createContext();

// New languages need to be added here
const I18N_LOCALES = Object.freeze({
  GB: 'en-GB',
  US: 'en-US',
});

export const withLocale = (Component) => {
  // find out the name of the wrapped component or use a default one
  const displayName = `withLocale(${fromComponentToName(Component)})`;

  const WithLocale = (props) => {
    const { locale } = useLocale();

    return <Component {...props} locale={locale} />;
  };

  // static fields must be copied from the wrapped to the wrapper
  hoistNonReactStatics(WithLocale, Component);

  // nice display name for the wrapper
  WithLocale.displayName = displayName;

  return WithLocale;
};

export const GiltProvider = ({ children }) => {
  const defaultLocale = determineUserLocale();

  const [locale, setLocale] = useState(defaultLocale);
  const { i18n } = useTranslation(defaultLocale);
  const { countryCode } = useSalonGilt();

  const formatDateShort = useCallback((date) => formatDateByLocale(date, { locale }), [locale]);

  const formatDateMid = useCallback((date) => formatDateByLocale(date, { locale, dateFormat: 'll' }), [locale]);

  const formatDateLong = useCallback((date) => formatDateByLocale(date, { locale, dateFormat: 'LL' }), [locale]);

  // Add locale and timezone
  // eslint-disable-next-line no-unused-vars
  const determineDayRange = (date) => determineDayRangeUTCForTimezone(date, 'en-US', 'Pacific/Honolulu');

  const formatTime = useCallback((date) => formatTimeByLocale(date, { locale }), [locale]);

  const changeLocale = useCallback(
    (newLocale) => {
      L.info('()', 'Changing locale in GILT provider from: ', locale, ' to: ', newLocale);

      if (!String(newLocale) || newLocale === i18n.language) {
        return;
      }

      lsSet(LSK.locale, newLocale);
      i18n.changeLanguage(newLocale);
      setLocale(newLocale);
    },
    [i18n, locale],
  );

  useEffect(() => {
    const i18nLocale = I18N_LOCALES[countryCode] ?? I18N_LOCALES.GB;

    if (!countryCode || countryCode === i18nLocale) {
      return;
    }

    changeLocale(i18nLocale);
  }, [changeLocale, countryCode]);

  const value = useMemo(
    () => ({ locale, changeLocale, formatDateShort, formatDateMid, formatDateLong, formatTime }),
    [locale, changeLocale, formatDateShort, formatDateMid, formatDateLong, formatTime],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useLocale = () => {
  const value = useContext(Context);

  return useMemo(() => value, [value]);
};

/**
 * Hook wrapper for translations.
 * Returns the same shape as react-i18next's useTranslation but allows
 * adding custom logic (logging, telemetry, fallback handling) in one place.
 */
export const useTranslation = (ns) => useLibraryTranslation(ns);
// Example: we could add logging here in the future
// const { t, i18n, ready } = useTranslation(ns);
// if (!ready) { console.debug('translation not ready', ns); }

/**
 * HOC wrapper for translations.
 * Usage: export default withTrans()(MyComponent)
 */
export const withTranslation = (...args) => withLibraryTranslation(...args);
